import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, Plus, LayoutDashboard, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import AdminResultsPanel from "@/components/AdminResultsPanel";
import AdminApprovalsPanel from "@/components/AdminApprovalsPanel";

function StatCard({ label, value }) {
  return (
    <div className="border border-jpa-border p-5 bg-white">
      <div className="text-[10px] font-bold uppercase tracking-widest text-jpa-primary">{label}</div>
      <div className="font-display text-5xl tracking-wider mt-1">{value ?? "—"}</div>
    </div>
  );
}

function ListManager({ title, items, columns, onDelete, onUpdate, form, setForm, fields, onCreate, testId }) {
  const [editingId, setEditingId] = useState(null);
  const startEdit = (it) => {
    setEditingId(it.id);
    const patch = {};
    fields.forEach((f) => {
      // Allow custom item→form mapping (used for tier: 2 → "Tier 2")
      if (typeof f.initialFromItem === "function") {
        patch[f.key] = f.initialFromItem(it);
        return;
      }
      // Special case: if key ends with `_str`, look for the array field
      if (f.key.endsWith("_str")) {
        const base = f.key.slice(0, -4);
        patch[f.key] = Array.isArray(it[base]) ? it[base].join(", ") : (it[base] ?? "");
      } else {
        patch[f.key] = it[f.key] ?? (f.type === "number" ? 0 : "");
      }
    });
    setForm(patch);
  };
  const cancelEdit = () => { setEditingId(null); };
  return (
    <div className="border border-jpa-border rounded-sm p-6">
      <h3 className="font-display text-2xl tracking-wider mb-4">{title}</h3>
      <div className="border border-jpa-border">
        <div className="grid gap-2 py-2 px-4 bg-jpa-surface text-[10px] font-bold uppercase tracking-widest" style={{ gridTemplateColumns: columns.map(() => "1fr").join(" ") + " 100px" }}>
          {columns.map((c) => <div key={c.key}>{c.label}</div>)}
          <div />
        </div>
        <div className="max-h-72 overflow-auto">
          {items.map((it) => (
            <div key={it.id} className={`grid gap-2 py-2 px-4 border-t border-jpa-border items-center text-sm ${editingId === it.id ? "bg-yellow-50" : ""}`} style={{ gridTemplateColumns: columns.map(() => "1fr").join(" ") + " 100px" }}>
              {columns.map((c) => <div key={c.key} className="truncate">{Array.isArray(it[c.key]) ? it[c.key].join(", ") : String(it[c.key] ?? "")}</div>)}
              <div className="flex gap-2 justify-end">
                {onUpdate && <button data-testid={`${testId}-edit-${it.id}`} onClick={() => startEdit(it)} className="text-jpa-primary hover:text-jpa-primary-hover" title="Edit"><Pencil className="w-4 h-4" /></button>}
                <button data-testid={`${testId}-delete-${it.id}`} onClick={() => onDelete(it.id)} className="text-jpa-live hover:text-red-700" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.key} className={f.fullWidth ? "md:col-span-2" : ""}>
            <Label>{f.label}</Label>
            {f.type === "textarea" ? (
              <Textarea rows={f.rows || 3} data-testid={`${testId}-field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1 rounded-sm" />
            ) : f.type === "select" ? (
              <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                <SelectTrigger data-testid={`${testId}-field-${f.key}`} className="mt-1 rounded-sm"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input type={f.type || "text"} data-testid={`${testId}-field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })} className="mt-1 rounded-sm" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        {editingId ? (
          <>
            <Button data-testid={`${testId}-save`} onClick={async () => { await onUpdate(editingId, form); setEditingId(null); }} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
              Save changes
            </Button>
            <Button data-testid={`${testId}-cancel`} onClick={cancelEdit} variant="outline" className="uppercase tracking-widest font-bold rounded-sm">
              Cancel
            </Button>
          </>
        ) : (
          <Button data-testid={`${testId}-add`} onClick={onCreate} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Add
          </Button>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [tournaments, setTournaments] = useState([]);
  const [news, setNews] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [messages, setMessages] = useState([]);
  const [entries, setEntries] = useState([]);
  const [matches, setMatches] = useState([]);
  const [stars, setStars] = useState([]);
  const [team, setTeam] = useState([]);
  const [districtsList, setDistrictsList] = useState([]);

  const [tForm, setTForm] = useState({ name: "", location: "", start_date: "", end_date: "", age_categories_str: "", event_categories_str: "", tier_display: "Tier 2", restrict_to_jharkhand: false, prize_pool: "", image_url: "", description: "", external_booking_url: "" });
  const [nForm, setNForm] = useState({ title: "", excerpt: "", body: "", category: "tournament", media_type: "image", image_url: "", video_url: "", author: "JPA Press" });
  const [vForm, setVForm] = useState({ title: "", description: "", youtube_id: "", category: "highlight" });
  const [sForm, setSForm] = useState({ name: "", tier: "partner", logo_url: "", website: "" });
  const [pForm, setPForm] = useState({ name: "", district: "Ranchi", gender: "M", ranking: 0, points: 0, wins: 0, losses: 0, titles: 0, image_url: "", club: "" });
  const [cForm, setCForm] = useState({ name: "", district: "Ranchi", address: "", contact_person: "", phone: "", courts: 2, image_url: "" });
  const [coForm, setCoForm] = useState({ name: "", certification: "Level 1", district: "Ranchi", experience_years: 1, phone: "", image_url: "" });
  const [mForm, setMForm] = useState({ tournament_id: "", player_user_id: "", opponent_name: "", score: "", result: "won", round: "R32", played_on: "" });
  const [bForm, setBForm] = useState({ title: "", body: "", user_id: "" });
  const [starForm, setStarForm] = useState({ image_url: "", name: "", caption: "", order: 0, active: true });
  const [teamForm, setTeamForm] = useState({ name: "", role: "", image_url: "", bio: "", order: 0 });
  const [distForm, setDistForm] = useState({ name: "", president: "", affiliated_since: 2019, order: 0 });

  const load = async () => {
    try {
      const [s, t, n, v, sp, p, cl, co, m, en, ma, st, tm, dl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/tournaments?include_media=1"),
        api.get("/news"),
        api.get("/videos"),
        api.get("/sponsors"),
        api.get("/players"),
        api.get("/clubs"),
        api.get("/coaches"),
        api.get("/admin/messages"),
        api.get("/admin/entries"),
        api.get("/admin/matches"),
        api.get("/jpa-stars?all=true"),
        api.get("/team"),
        api.get("/districts"),
      ]);
      setStats(s.data); setTournaments(t.data); setNews(n.data); setVideos(v.data); setSponsors(sp.data);
      setPlayers(p.data); setClubs(cl.data); setCoaches(co.data); setMessages(m.data);
      setEntries(en.data); setMatches(ma.data); setStars(st.data);
      setTeam(tm.data); setDistrictsList(dl.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const handle = async (fn) => {
    try { await fn(); toast.success("Saved"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-14">
      <div className="flex items-center gap-3 mb-2">
        <LayoutDashboard className="w-6 h-6 text-jpa-primary" />
        <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Admin panel</div>
      </div>
      <h1 className="font-display text-6xl md:text-7xl tracking-wider">JPA CONTROL ROOM</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard label="Players" value={stats.players} />
        <StatCard label="Tournaments" value={stats.tournaments} />
        <StatCard label="News" value={stats.news} />
        <StatCard label="Videos" value={stats.videos} />
        <StatCard label="Sponsors" value={stats.sponsors} />
        <StatCard label="Clubs" value={stats.clubs} />
        <StatCard label="Coaches" value={stats.coaches} />
        <StatCard label="Users" value={stats.users} />
        <StatCard label="Messages" value={stats.messages} />
      </div>

      <Tabs defaultValue="tournaments" className="mt-10">
        <TabsList className="bg-jpa-surface rounded-sm h-auto p-1 flex-wrap">
          {["approvals", "tournaments", "quick-entries", "results", "entries", "matches", "news", "videos", "sponsors", "stars", "team", "districts", "players", "clubs", "coaches", "messages", "broadcast"].map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="uppercase tracking-widest text-xs font-bold data-[state=active]:bg-jpa-primary data-[state=active]:text-white rounded-sm px-4 py-2 capitalize">{k}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="approvals" className="mt-6">
          <AdminApprovalsPanel onChange={load} />
        </TabsContent>

        <TabsContent value="tournaments" className="mt-6">
          <ListManager
            testId="admin-tournament"
            title="Create JPA tournament (auto-sanctioned as State · Tier 2)"
            items={tournaments}
            columns={[{ key: "name", label: "Name" }, { key: "location", label: "Location" }, { key: "tier", label: "Tier" }, { key: "division", label: "Division" }, { key: "approval_status", label: "Approval" }]}
            form={tForm} setForm={setTForm}
            fields={[
              { key: "name", label: "Name" }, { key: "location", label: "Location" },
              { key: "start_date", label: "Start date (YYYY-MM-DD)" }, { key: "end_date", label: "End date (YYYY-MM-DD)" },
              { key: "age_categories_str", label: "Age category (comma-separated: U-10, U-12, U-14, U-16, 19+, 35+, 50+, 60+)", fullWidth: true },
              { key: "event_categories_str", label: "Event category (comma-separated: mens_singles, mens_doubles, womens_singles, womens_doubles, mixed_doubles)", fullWidth: true },
              { key: "registration_open_display", label: "Registration status",
                type: "select", options: ["Open", "Closed"],
                initialFromItem: (it) => it.registration_open === false ? "Closed" : "Open" },
              { key: "tier_display", label: "Tier (drives AIPA ranking-point matrix)",
                type: "select", options: ["No tier", "Tier 1", "Tier 2", "Tier 3"],
                initialFromItem: (it) => it.tier == null ? "No tier" : `Tier ${it.tier}` },
              { key: "restrict_to_jharkhand", label: "Restrict to Jharkhand players only",
                type: "select", options: ["No — open to all", "Yes — Jharkhand only"],
                initialFromItem: (it) => it.restrict_to_jharkhand ? "Yes — Jharkhand only" : "No — open to all" },
              { key: "prize_pool", label: "Prize pool" }, { key: "image_url", label: "Image URL (portrait 2:3, e.g. 800×1200)" },
              { key: "external_booking_url", label: "External booking URL (optional — for National/International)" },
              { key: "description", label: "Description (details, venue notes, contact info…)", type: "textarea", rows: 5, fullWidth: true },
            ]}
            onCreate={() => handle(async () => {
              const tier = tForm.tier_display === "No tier" ? null : parseInt(String(tForm.tier_display).replace("Tier ", ""), 10) || null;
              const payload = { ...tForm, tier,
                registration_open: tForm.registration_open_display !== "Closed",
                restrict_to_jharkhand: tForm.restrict_to_jharkhand === "Yes — Jharkhand only",
                age_categories: (tForm.age_categories_str || "").split(",").map((s) => s.trim()).filter(Boolean),
                event_categories: (tForm.event_categories_str || "").split(",").map((s) => s.trim()).filter(Boolean),
              };
              delete payload.age_categories_str; delete payload.event_categories_str; delete payload.tier_display; delete payload.registration_open_display;
              await api.post("/tournaments", payload);
              setTForm({ name: "", location: "", start_date: "", end_date: "", age_categories_str: "", event_categories_str: "", tier_display: "Tier 2", registration_open_display: "Open", restrict_to_jharkhand: false, prize_pool: "", image_url: "", description: "", external_booking_url: "" });
            })}
            onUpdate={(id, formVals) => handle(async () => {
              const existing = tournaments.find((t) => t.id === id) || {};
              const tier = formVals.tier_display === "No tier" ? null : parseInt(String(formVals.tier_display).replace("Tier ", ""), 10) || null;
              const payload = { ...existing, ...formVals, tier,
                registration_open: formVals.registration_open_display !== "Closed",
                restrict_to_jharkhand: formVals.restrict_to_jharkhand === "Yes — Jharkhand only",
                age_categories: (formVals.age_categories_str || "").split(",").map((s) => s.trim()).filter(Boolean),
                event_categories: (formVals.event_categories_str || "").split(",").map((s) => s.trim()).filter(Boolean),
              };
              delete payload.age_categories_str; delete payload.event_categories_str; delete payload.tier_display; delete payload.registration_open_display; delete payload.id;
              await api.put(`/tournaments/${id}`, payload);
              setTForm({ name: "", location: "", start_date: "", end_date: "", age_categories_str: "", event_categories_str: "", tier_display: "Tier 2", registration_open_display: "Open", restrict_to_jharkhand: false, prize_pool: "", image_url: "", description: "", external_booking_url: "" });
            })}
            onDelete={(id) => handle(() => api.delete(`/tournaments/${id}`))}
          />
          <QuickRegistrationSection tournaments={tournaments} onSaved={load} />
        </TabsContent>

        <TabsContent value="results" className="mt-6">
          <AdminResultsPanel tournaments={tournaments} players={players} />
        </TabsContent>

        <TabsContent value="quick-entries" className="mt-6">
          <QuickEntriesPanel tournaments={tournaments} />
        </TabsContent>

        <TabsContent value="news" className="mt-6">
          <ListManager
            testId="admin-news" title="Manage news" items={news}
            columns={[{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "media_type", label: "Media" }, { key: "author", label: "Author" }]}
            form={nForm} setForm={setNForm}
            fields={[
              { key: "title", label: "Title" }, { key: "excerpt", label: "Excerpt" }, { key: "body", label: "Body", type: "textarea" },
              { key: "category", label: "Category", type: "select", options: ["tournament", "national", "international"] },
              { key: "media_type", label: "Media type", type: "select", options: ["image", "video", "text"] },
              { key: "image_url", label: "Image URL" }, { key: "video_url", label: "YouTube URL (optional)" }, { key: "author", label: "Author" },
            ]}
            onCreate={() => handle(async () => { await api.post("/news", nForm); setNForm({ title: "", excerpt: "", body: "", category: "tournament", media_type: "image", image_url: "", video_url: "", author: "JPA Press" }); })}
            onDelete={(id) => handle(() => api.delete(`/news/${id}`))}
          />
        </TabsContent>

        <TabsContent value="videos" className="mt-6">
          <ListManager
            testId="admin-video" title="Manage videos" items={videos}
            columns={[{ key: "title", label: "Title" }, { key: "youtube_id", label: "YouTube ID" }, { key: "category", label: "Category" }]}
            form={vForm} setForm={setVForm}
            fields={[
              { key: "title", label: "Title" }, { key: "description", label: "Description" },
              { key: "youtube_id", label: "YouTube video ID (e.g. dQw4w9WgXcQ)" },
              { key: "category", label: "Category", type: "select", options: ["highlight", "coaching", "full-match", "behind-the-scenes"] },
            ]}
            onCreate={() => handle(async () => { await api.post("/videos", vForm); setVForm({ title: "", description: "", youtube_id: "", category: "highlight" }); })}
            onDelete={(id) => handle(() => api.delete(`/videos/${id}`))}
          />
        </TabsContent>

        <TabsContent value="sponsors" className="mt-6">
          <ListManager
            testId="admin-sponsor" title="Manage sponsors" items={sponsors}
            columns={[{ key: "name", label: "Name" }, { key: "tier", label: "Tier" }, { key: "website", label: "Website" }]}
            form={sForm} setForm={setSForm}
            fields={[
              { key: "name", label: "Name" },
              { key: "tier", label: "Tier", type: "select", options: ["prime", "partner", "supplier"] },
              { key: "logo_url", label: "Logo URL" }, { key: "website", label: "Website" },
            ]}
            onCreate={() => handle(async () => { await api.post("/sponsors", sForm); setSForm({ name: "", tier: "partner", logo_url: "", website: "" }); })}
            onDelete={(id) => handle(() => api.delete(`/sponsors/${id}`))}
          />
        </TabsContent>

        <TabsContent value="stars" className="mt-6">
          <ListManager
            testId="admin-star"
            title={`JPA Stars — homepage carousel (${stars.length}/10 configured)`}
            items={stars}
            columns={[{ key: "order", label: "Order" }, { key: "name", label: "Name" }, { key: "caption", label: "Caption" }, { key: "active", label: "Active" }]}
            form={starForm} setForm={setStarForm}
            fields={[
              { key: "image_url", label: "Image URL (portrait 3:4 recommended, e.g. 600×800)" },
              { key: "name", label: "Name (optional)" },
              { key: "caption", label: "Caption (optional, e.g. State Champion 2025)" },
              { key: "order", label: "Sort order (lower = first)", type: "number" },
              { key: "active", label: "Active", type: "select", options: [true, false].map(String) },
            ]}
            onCreate={() => handle(async () => {
              const payload = { ...starForm, active: String(starForm.active) !== "false", order: Number(starForm.order) || 0 };
              await api.post("/jpa-stars", payload);
              setStarForm({ image_url: "", name: "", caption: "", order: 0, active: true });
            })}
            onDelete={(id) => handle(() => api.delete(`/jpa-stars/${id}`))}
          />
          <div className="mt-3 text-xs text-jpa-muted">Only the first 10 active entries (sorted by order asc) render on the homepage carousel.</div>
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <ListManager
            testId="admin-team"
            title={`Team behind — About page (${team.length} members)`}
            items={team}
            columns={[{ key: "order", label: "Order" }, { key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "bio", label: "Bio" }]}
            form={teamForm} setForm={setTeamForm}
            fields={[
              { key: "name", label: "Full name" },
              { key: "role", label: "Role (e.g. President, Secretary General)" },
              { key: "image_url", label: "Photo URL (portrait 4:5 recommended)" },
              { key: "bio", label: "Short bio" },
              { key: "order", label: "Sort order (lower = first)", type: "number" },
            ]}
            onCreate={() => handle(async () => {
              await api.post("/team", { ...teamForm, order: Number(teamForm.order) || 0 });
              setTeamForm({ name: "", role: "", image_url: "", bio: "", order: 0 });
            })}
            onDelete={(id) => handle(() => api.delete(`/team/${id}`))}
          />
          <div className="mt-3 text-xs text-jpa-muted">These members show up on <b>/about?tab=team</b>.</div>
        </TabsContent>

        <TabsContent value="districts" className="mt-6">
          <ListManager
            testId="admin-district"
            title={`Affiliated Districts (${districtsList.length})`}
            items={districtsList}
            columns={[{ key: "order", label: "Order" }, { key: "name", label: "District" }, { key: "president", label: "President" }, { key: "affiliated_since", label: "Since" }]}
            form={distForm} setForm={setDistForm}
            fields={[
              { key: "name", label: "District name" },
              { key: "president", label: "District president" },
              { key: "affiliated_since", label: "Affiliated since (year)", type: "number" },
              { key: "order", label: "Sort order (lower = first)", type: "number" },
            ]}
            onCreate={() => handle(async () => {
              await api.post("/districts", { ...distForm, affiliated_since: Number(distForm.affiliated_since) || 2019, order: Number(distForm.order) || 0 });
              setDistForm({ name: "", president: "", affiliated_since: 2019, order: 0 });
            })}
            onDelete={(id) => handle(() => api.delete(`/districts/${id}`))}
          />
          <div className="mt-3 text-xs text-jpa-muted">Districts appear on <b>/about?tab=districts</b> and in club/player registration dropdowns.</div>
        </TabsContent>

        <TabsContent value="players" className="mt-6">
          <PlayerManagerPanel players={players} onChanged={load} />
        </TabsContent>

        <TabsContent value="clubs" className="mt-6">
          <ListManager
            testId="admin-club" title="Manage clubs" items={clubs}
            columns={[{ key: "name", label: "Name" }, { key: "district", label: "District" }, { key: "courts", label: "Courts" }]}
            form={cForm} setForm={setCForm}
            fields={[
              { key: "name", label: "Name" }, { key: "district", label: "District" },
              { key: "address", label: "Address" }, { key: "contact_person", label: "Contact person" },
              { key: "phone", label: "Phone" }, { key: "courts", label: "Courts", type: "number" }, { key: "image_url", label: "Image URL" },
            ]}
            onCreate={() => handle(async () => { await api.post("/clubs", cForm); setCForm({ name: "", district: "Ranchi", address: "", contact_person: "", phone: "", courts: 2, image_url: "" }); })}
            onDelete={(id) => handle(() => api.delete(`/clubs/${id}`))}
          />
        </TabsContent>

        <TabsContent value="coaches" className="mt-6">
          <ListManager
            testId="admin-coach" title="Manage coaches" items={coaches}
            columns={[{ key: "name", label: "Name" }, { key: "certification", label: "Cert" }, { key: "district", label: "District" }, { key: "experience_years", label: "Yrs" }]}
            form={coForm} setForm={setCoForm}
            fields={[
              { key: "name", label: "Name" }, { key: "certification", label: "Certification" },
              { key: "district", label: "District" }, { key: "experience_years", label: "Experience (years)", type: "number" },
              { key: "phone", label: "Phone" }, { key: "image_url", label: "Image URL" },
            ]}
            onCreate={() => handle(async () => { await api.post("/coaches", coForm); setCoForm({ name: "", certification: "Level 1", district: "Ranchi", experience_years: 1, phone: "", image_url: "" }); })}
            onDelete={(id) => handle(() => api.delete(`/coaches/${id}`))}
          />
        </TabsContent>

        <TabsContent value="entries" className="mt-6">
          <div className="border border-jpa-border rounded-sm p-6">
            <h3 className="font-display text-2xl tracking-wider mb-4">Tournament entries ({entries.length})</h3>
            <div className="max-h-[70vh] overflow-auto divide-y divide-jpa-border">
              {entries.length === 0 && <div className="text-jpa-muted text-sm">No entries yet.</div>}
              {entries.map((e) => (
                <div key={e.id} data-testid={`admin-entry-${e.id}`} className="py-3 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold">{e.tournament_name}</div>
                    <div className="text-xs text-jpa-muted">{e.player_name} • {e.player_email} • {e.category}</div>
                    <div className="text-[10px] uppercase tracking-widest text-jpa-muted">{new Date(e.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm ${e.status === "approved" ? "bg-green-100 text-green-800" : e.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>{e.status}</span>
                  {e.status === "pending" && (
                    <>
                      <Button data-testid={`admin-entry-approve-${e.id}`} size="sm" onClick={() => handle(() => api.post(`/admin/entries/${e.id}/approve`))} className="bg-jpa-primary text-white rounded-sm">Approve</Button>
                      <Button data-testid={`admin-entry-reject-${e.id}`} size="sm" variant="outline" onClick={() => handle(() => api.post(`/admin/entries/${e.id}/reject`))} className="rounded-sm">Reject</Button>
                    </>
                  )}
                  <button data-testid={`admin-entry-delete-${e.id}`} onClick={() => handle(() => api.delete(`/admin/entries/${e.id}`))} className="text-jpa-live"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="matches" className="mt-6">
          <div className="border border-jpa-border rounded-sm p-6">
            <h3 className="font-display text-2xl tracking-wider mb-4">Match results ({matches.length})</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Tournament</Label>
                <Select value={mForm.tournament_id} onValueChange={(v) => setMForm({ ...mForm, tournament_id: v })}>
                  <SelectTrigger data-testid="admin-match-tournament" className="mt-1 rounded-sm"><SelectValue placeholder="Select tournament" /></SelectTrigger>
                  <SelectContent>{tournaments.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Player User ID (jpa user_id)</Label>
                <Input data-testid="admin-match-user" value={mForm.player_user_id} onChange={(e) => setMForm({ ...mForm, player_user_id: e.target.value })} placeholder="user_XXXXXXXXXXXX" className="mt-1 rounded-sm" />
              </div>
              <div><Label>Opponent</Label><Input data-testid="admin-match-opponent" value={mForm.opponent_name} onChange={(e) => setMForm({ ...mForm, opponent_name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Score</Label><Input data-testid="admin-match-score" value={mForm.score} onChange={(e) => setMForm({ ...mForm, score: e.target.value })} placeholder="11-8, 11-9" className="mt-1 rounded-sm" /></div>
              <div>
                <Label>Result</Label>
                <Select value={mForm.result} onValueChange={(v) => setMForm({ ...mForm, result: v })}>
                  <SelectTrigger data-testid="admin-match-result" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="won">Won</SelectItem><SelectItem value="lost">Lost</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Round</Label><Input data-testid="admin-match-round" value={mForm.round} onChange={(e) => setMForm({ ...mForm, round: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Date played</Label><Input data-testid="admin-match-date" type="date" value={mForm.played_on} onChange={(e) => setMForm({ ...mForm, played_on: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
            <Button data-testid="admin-match-add" onClick={() => handle(async () => { await api.post("/admin/matches", mForm); setMForm({ tournament_id: "", player_user_id: "", opponent_name: "", score: "", result: "won", round: "R32", played_on: "" }); })} className="mt-4 bg-jpa-primary text-white uppercase tracking-widest font-bold rounded-sm"><Plus className="w-4 h-4 mr-2" /> Add match</Button>

            <div className="mt-6 max-h-[50vh] overflow-auto divide-y divide-jpa-border">
              {matches.map((m) => (
                <div key={m.id} data-testid={`admin-match-${m.id}`} className="py-3 flex flex-wrap items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm">{m.tournament_name} • {m.round}</div>
                    <div className="text-xs text-jpa-muted">vs {m.opponent_name} • {m.score} • {m.played_on}</div>
                    <div className="text-[10px] uppercase tracking-widest text-jpa-muted">User: {m.player_user_id}</div>
                  </div>
                  <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm ${m.result === "won" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{m.result}</span>
                  <button onClick={() => handle(() => api.delete(`/admin/matches/${m.id}`))} data-testid={`admin-match-delete-${m.id}`} className="text-jpa-live"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="messages" className="mt-6">
          <div className="border border-jpa-border rounded-sm p-6">
            <h3 className="font-display text-2xl tracking-wider mb-4">Contact messages</h3>
            <div className="divide-y divide-jpa-border">
              {messages.length === 0 && <div className="text-jpa-muted text-sm">No messages yet.</div>}
              {messages.map((m) => (
                <div key={m.id} data-testid={`admin-message-${m.id}`} className="py-4">
                  <div className="text-xs uppercase tracking-widest text-jpa-primary">{new Date(m.created_at).toLocaleString()}</div>
                  <div className="font-bold text-lg">{m.subject}</div>
                  <div className="text-sm text-jpa-muted">{m.name} • {m.email} • {m.phone || "—"}</div>
                  <p className="mt-2 text-sm">{m.message}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="broadcast" className="mt-6">
          <div className="border border-jpa-border rounded-sm p-6 max-w-2xl">
            <h3 className="font-display text-2xl tracking-wider mb-1">Broadcast notification</h3>
            <p className="text-sm text-jpa-muted mb-4">Leave user_id empty to broadcast to all users.</p>
            <div className="grid gap-3">
              <div><Label>Title</Label><Input data-testid="admin-broadcast-title" value={bForm.title} onChange={(e) => setBForm({ ...bForm, title: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Body</Label><Textarea data-testid="admin-broadcast-body" value={bForm.body} onChange={(e) => setBForm({ ...bForm, body: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>User ID (optional)</Label><Input data-testid="admin-broadcast-user" value={bForm.user_id} onChange={(e) => setBForm({ ...bForm, user_id: e.target.value })} placeholder="user_XXXXXXXXXXXX" className="mt-1 rounded-sm" /></div>
            </div>
            <Button data-testid="admin-broadcast-send" onClick={() => handle(async () => {
              const payload = { title: bForm.title, body: bForm.body, type: "announcement" };
              if (bForm.user_id) payload.user_id = bForm.user_id;
              await api.post("/admin/notifications/broadcast", payload);
              setBForm({ title: "", body: "", user_id: "" });
            })} className="mt-4 bg-jpa-primary text-white uppercase tracking-widest font-bold rounded-sm">Send notification</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ---------- Quick Registration setup (per-tournament) ----------
const QR_AGE_OPTIONS = ["U-10", "U-12", "U-14", "U-16", "U-19", "19+", "35+", "50+", "60+"];
const QR_EVENT_OPTIONS = [
  { v: "mens_singles", l: "Men's Singles" },
  { v: "mens_doubles", l: "Men's Doubles" },
  { v: "womens_singles", l: "Women's Singles" },
  { v: "womens_doubles", l: "Women's Doubles" },
  { v: "mixed_doubles", l: "Mixed Doubles" },
];

function QuickRegistrationSection({ tournaments, onSaved }) {
  const [tid, setTid] = useState("");
  const [conf, setConf] = useState({
    quick_registration: false,
    qr_max_events: 3,
    qr_fee_tiers: { "1": 750, "2": 1500, "3": 2000 },
    qr_coordinator_whatsapp: "",
    qr_age_categories: [],
    qr_event_categories: [],
  });
  const t = tournaments.find((x) => x.id === tid);

  useEffect(() => {
    if (!t) return;
    setConf({
      quick_registration: !!t.quick_registration,
      qr_max_events: t.qr_max_events || 3,
      qr_fee_tiers: t.qr_fee_tiers || { "1": 750, "2": 1500, "3": 2000 },
      qr_coordinator_whatsapp: t.qr_coordinator_whatsapp || "",
      qr_age_categories: t.qr_age_categories || [],
      qr_event_categories: t.qr_event_categories || [],
    });
  }, [t]);

  const toggleAge = (a) => setConf((c) => ({ ...c, qr_age_categories: c.qr_age_categories.includes(a) ? c.qr_age_categories.filter((x) => x !== a) : [...c.qr_age_categories, a] }));
  const toggleEvent = (e) => setConf((c) => ({ ...c, qr_event_categories: c.qr_event_categories.includes(e) ? c.qr_event_categories.filter((x) => x !== e) : [...c.qr_event_categories, e] }));
  const setTier = (n, v) => setConf((c) => ({ ...c, qr_fee_tiers: { ...c.qr_fee_tiers, [String(n)]: Number(v) || 0 } }));

  const save = async () => {
    if (!tid) return toast.error("Pick a tournament first");
    try {
      await api.put(`/tournaments/${tid}`, {
        ...t,
        quick_registration: conf.quick_registration,
        qr_max_events: Number(conf.qr_max_events) || 3,
        qr_fee_tiers: conf.qr_fee_tiers,
        qr_coordinator_whatsapp: conf.qr_coordinator_whatsapp,
        qr_age_categories: conf.qr_age_categories,
        qr_event_categories: conf.qr_event_categories,
      });
      toast.success("Quick-registration settings saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const feeRows = Array.from({ length: Number(conf.qr_max_events) || 3 }, (_, i) => i + 1);

  return (
    <div data-testid="quick-reg-section" className="border border-jpa-border rounded-sm p-6 mt-6 bg-jpa-surface">
      <h3 className="font-display text-2xl tracking-wider mb-1">Quick registration setup</h3>
      <p className="text-xs text-jpa-muted mb-4">Enable the no-JPA-ID JOIN NOW form for a specific tournament. Everything else works as usual.</p>

      <Label className="text-xs">Select tournament</Label>
      <Select value={tid} onValueChange={setTid}>
        <SelectTrigger data-testid="qr-tournament-select" className="mt-1 rounded-sm max-w-md"><SelectValue placeholder="Pick a tournament to configure…" /></SelectTrigger>
        <SelectContent>{tournaments.map((tt) => <SelectItem key={tt.id} value={tt.id}>{tt.name} · {tt.location}</SelectItem>)}</SelectContent>
      </Select>

      {t && (
        <>
          <div className="mt-4 p-3 bg-white border border-jpa-border rounded-sm flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${t.registration_open !== false ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`}></span>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-jpa-ink">
                  Registration status: <span className={t.registration_open !== false ? "text-emerald-700 font-extrabold" : "text-red-700 font-extrabold"}>{t.registration_open !== false ? "OPEN" : "CLOSED"}</span>
                </div>
                <div className="text-[11px] text-jpa-muted">{t.registration_open !== false ? "Players can submit registrations" : "Registration form is locked and blocking submissions"}</div>
              </div>
            </div>
            <Button
              data-testid="toggle-reg-status-btn"
              size="sm"
              variant={t.registration_open !== false ? "destructive" : "default"}
              onClick={async () => {
                const action = t.registration_open !== false ? "CLOSE" : "OPEN";
                if (!window.confirm(`${action} registration for "${t.name}"?`)) return;
                try {
                  const r = await api.post(`/admin/tournaments/${t.id}/toggle-registration`, { registration_open: t.registration_open === false });
                  toast.success(`Registration is now ${r.data.registration_open ? "OPEN" : "CLOSED"}`);
                  onSaved?.();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }}
              className="rounded-sm uppercase tracking-widest text-xs font-bold"
            >
              {t.registration_open !== false ? "🔴 Close Registration" : "🟢 Open Registration"}
            </Button>
          </div>

          <label className="mt-4 flex items-center gap-2 cursor-pointer text-sm">
            <input type="checkbox" checked={conf.quick_registration} onChange={(e) => setConf((c) => ({ ...c, quick_registration: e.target.checked }))} data-testid="qr-enable" className="w-4 h-4 accent-jpa-primary" />
            <b>Enable quick-registration for this tournament</b>
            <span className="text-jpa-muted text-xs">(the JOIN NOW button will use the public quick-form)</span>
          </label>

          {conf.quick_registration && (
            <div className="mt-5 grid gap-6 md:grid-cols-2">
              <div>
                <Label>Max events per player</Label>
                <Input type="number" min={1} max={10} value={conf.qr_max_events} onChange={(e) => setConf((c) => ({ ...c, qr_max_events: e.target.value }))} data-testid="qr-max-events" className="mt-1 rounded-sm max-w-[120px]" />
                <Label className="mt-4 block">Fee tiers (₹ per selected count)</Label>
                <div className="mt-1 space-y-2">
                  {feeRows.map((n) => (
                    <div key={n} className="flex items-center gap-2">
                      <span className="text-xs text-jpa-muted w-24">{n} event{n > 1 ? "s" : ""}</span>
                      <Input type="number" value={conf.qr_fee_tiers[String(n)] ?? 0} onChange={(e) => setTier(n, e.target.value)} data-testid={`qr-fee-${n}`} className="rounded-sm max-w-[140px]" />
                    </div>
                  ))}
                </div>
                <Label className="mt-4 block">Add-on fee per extra event (₹)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    data-testid="qr-addon-fee"
                    defaultValue={t.qr_addon_fee ?? 500}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value, 10) || 500;
                      if ((t.qr_addon_fee ?? 500) === val) return;
                      api.put(`/tournaments/${tid}`, { ...t, qr_addon_fee: val })
                        .then(() => { toast.success("Add-on fee saved"); onSaved?.(); })
                        .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
                    }}
                    className="rounded-sm max-w-[140px]"
                  />
                  <span className="text-[11px] text-jpa-muted">charged per event when a player re-registers</span>
                </div>
                <Label className="mt-4 block">Coordinator WhatsApp</Label>
                <Input placeholder="+91 …" value={conf.qr_coordinator_whatsapp} onChange={(e) => setConf((c) => ({ ...c, qr_coordinator_whatsapp: e.target.value }))} data-testid="qr-coord-wa" className="mt-1 rounded-sm max-w-md" />
                <Label className="mt-4 block">Payment QR image</Label>
                {t.payment_qr_data_url && <img src={t.payment_qr_data_url} alt="QR" className="mt-2 w-32 h-32 object-contain border border-jpa-border bg-white" />}
                <input
                  type="file"
                  accept="image/*"
                  data-testid="qr-payment-upload"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
                    const r = new FileReader();
                    r.onload = () => {
                      // patch tournament directly
                      api.put(`/tournaments/${tid}`, { ...t, payment_qr_data_url: r.result })
                        .then(() => { toast.success("Payment QR uploaded"); onSaved?.(); })
                        .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
                    };
                    r.readAsDataURL(f);
                  }}
                  className="mt-2 block text-xs"
                />
                <Label className="mt-4 block">UPI ID / VPA <span className="text-jpa-muted text-[10px] font-normal">(shown next to the QR — default: pla1447@fbl)</span></Label>
                <div className="mt-1 flex items-center gap-2 max-w-md">
                  <Input
                    data-testid="qr-upi-id"
                    defaultValue={t.upi_id || ""}
                    placeholder="e.g. yourclub@upi"
                    className="rounded-sm font-mono flex-1"
                    onBlur={(e) => {
                      const val = (e.target.value || "").trim();
                      if ((t.upi_id || "") === val) return;
                      api.put(`/tournaments/${tid}`, { ...t, upi_id: val || null })
                        .then(() => { toast.success("UPI ID saved"); onSaved?.(); })
                        .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
                    }}
                  />
                </div>
              </div>
              <div>
                <Label>Age categories to expose</Label>
                <div className="mt-1 grid grid-cols-2 gap-1">
                  {QR_AGE_OPTIONS.map((a) => (
                    <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={conf.qr_age_categories.includes(a)} onChange={() => toggleAge(a)} data-testid={`qr-age-${a}`} className="w-4 h-4 accent-jpa-primary" />
                      {a}
                    </label>
                  ))}
                </div>
                <Label className="mt-4 block">Event categories to expose</Label>
                <div className="mt-1 space-y-1">
                  {QR_EVENT_OPTIONS.map((e) => (
                    <label key={e.v} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={conf.qr_event_categories.includes(e.v)} onChange={() => toggleEvent(e.v)} data-testid={`qr-evt-${e.v}`} className="w-4 h-4 accent-jpa-primary" />
                      {e.l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Button onClick={save} data-testid="qr-save" className="mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
            Save quick-registration settings
          </Button>
        </>
      )}
    </div>
  );
}

function QuickEntriesPanel({ tournaments }) {
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [entries, setEntries] = useState([]);
  const [receiptModal, setReceiptModal] = useState(null);
  const [registeredModal, setRegisteredModal] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteAadhaar, setDeleteAadhaar] = useState(true);
  const restoreInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [dupGroups, setDupGroups] = useState(null);
  const findDuplicates = async () => {
    try {
      const params = filter !== "all" ? `?tournament_id=${filter}` : "";
      const r = await api.get(`/admin/quick-entries/duplicates${params}`);
      setDupGroups(r.data);
      if (!r.data.total_groups) toast.success("No duplicates found — all clean!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed"); }
  };
  const dedupeGroup = async (group) => {
    const sorted = [...group.entries].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const keep = sorted[0];
    const rest = sorted.slice(1);
    if (!rest.length) return;
    if (!window.confirm(`Keep "${keep.name}" (oldest) and delete ${rest.length} duplicate(s)?\nTheir tournament entries will be removed too.`)) return;
    try {
      await api.post(`/admin/quick-entries/dedupe`, { keep_id: keep.id, delete_ids: rest.map((x) => x.id) });
      toast.success(`Deleted ${rest.length} duplicate(s), kept ${keep.name}`);
      setDupGroups(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Dedupe failed"); }
  };
  const resolveBulkTournament = () => {
    if (filter !== "all") return filter;
    const qrTs = tournaments.filter((t) => t.quick_registration);
    if (qrTs.length === 1) return qrTs[0].id;
    toast.info(qrTs.length === 0 ? "No tournament has quick registration enabled" : "Pick a specific tournament first");
    return null;
  };
  const downloadBulkTemplate = async () => {
    const tid = resolveBulkTournament();
    if (!tid) return;
    try {
      const r = await api.get(`/admin/quick-entries/bulk-template.csv?tournament_id=${tid}`, { responseType: "blob" });
      downloadBlob(r.data, "jpa-bulk-upload-template.csv");
      toast.success("Sample CSV downloaded — fill it and upload");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
  };
  const onBulkUpload = async (file) => {
    if (!file) return;
    const tid = resolveBulkTournament();
    if (!tid) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/quick-entries/bulk-upload?tournament_id=${tid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(r.data);
      toast.success(`Bulk upload: ${r.data.created} created, ${r.data.skipped} duplicates skipped, ${r.data.errors?.length || 0} errors`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
  };
  const load = () => api.get(`/admin/quick-entries${filter !== "all" ? `?tournament_id=${filter}` : ""}`).then((r) => setEntries(r.data));
  useEffect(() => { load(); }, [filter]);
  const visibleEntries = entries.filter((e) => {
    if (statusFilter !== "all" && e.status !== statusFilter) return false;
    if (ageFilter !== "all" && !(e.selections || []).some((s) => s.age_category === ageFilter)) return false;
    if (eventFilter !== "all" && !(e.selections || []).some((s) => s.event_category === eventFilter)) return false;
    return true;
  });
  const ageSortKey = (a) => {
    a = a || "";
    if (a.startsWith("U-")) { const n = parseInt(a.slice(2), 10); return [0, isNaN(n) ? 999 : n]; }
    if (a.endsWith("+")) { const n = parseInt(a.slice(0, -1), 10); return [2, isNaN(n) ? 999 : n]; }
    return [1, 0];
  };
  const availableAges = Array.from(new Set(entries.flatMap((e) => (e.selections || []).map((s) => s.age_category)).filter(Boolean)))
    .sort((a, b) => { const ka = ageSortKey(a), kb = ageSortKey(b); return ka[0] - kb[0] || ka[1] - kb[1] || a.localeCompare(b); });
  const availableEvents = Array.from(new Set(entries.flatMap((e) => (e.selections || []).map((s) => s.event_category)).filter(Boolean))).sort();
  // Raw event-entry count: sum of selections across visible registrations.
  const eventEntryCount = visibleEntries.reduce((n, e) => n + (e.selections || []).length, 0);
  // Pair-adjusted count: dedupe doubles pairs across quick-entry docs
  // (Aadhaar link OR reciprocal partner-name match within same event+age).
  const pairAdjustedCount = (() => {
    const seenPairs = new Set();
    const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
    const byAad = new Map();
    visibleEntries.forEach((e) => { if (e.aadhaar_number && e.tournament_id) byAad.set(`${e.tournament_id}__${e.aadhaar_number}`, e); });
    let n = 0;
    visibleEntries.forEach((e) => {
      (e.selections || []).forEach((s) => {
        const ec = s.event_category || "", ac = s.age_category || "";
        if (ageFilter !== "all" && ac !== ageFilter) return;
        if (eventFilter !== "all" && ec !== eventFilter) return;
        if (ec.includes("doubles")) {
          let other = s.partner_aadhaar ? byAad.get(`${e.tournament_id}__${s.partner_aadhaar}`) : null;
          if (!other && s.partner_name) {
            const pn = norm(s.partner_name);
            other = visibleEntries.find((o) => o.id !== e.id && o.tournament_id === e.tournament_id
              && norm(o.name) === pn
              && (o.selections || []).some((os) => os.event_category === ec && os.age_category === ac)) || null;
          }
          if (other && other.id !== e.id) {
            const key = [e.id, other.id].sort().join("|") + `|${ec}|${ac}`;
            if (seenPairs.has(key)) return;
            seenPairs.add(key);
          }
        }
        n += 1;
      });
    });
    return n;
  })();
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("tournament_id", filter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/admin/quick-entries/export.csv?${params.toString()}`, { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(r.data, `jpa-quick-entries-${stamp}.csv`);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
  };
  const exportReport = async (kind /* "csv" | "pdf" */) => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("tournament_id", filter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const url = kind === "pdf" ? "/admin/quick-entries/report.pdf" : "/admin/quick-entries/report.csv";
      const r = await api.get(`${url}?${params.toString()}`, { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(r.data, `jpa-entries-report-${stamp}.${kind}`);
      toast.success(`${kind.toUpperCase()} report downloaded`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Report failed"); }
  };
  const exportJson = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("tournament_id", filter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/admin/quick-entries/export.json?${params.toString()}`, { responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      downloadBlob(r.data, `jpa-quick-entries-backup-${stamp}.json`);
      toast.success("JSON backup downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backup failed"); }
  };
  const onRestoreFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const list = Array.isArray(parsed?.entries) ? parsed.entries : (Array.isArray(parsed) ? parsed : null);
      if (!list) { toast.error("Invalid backup file — no entries array"); return; }
      const mode = window.confirm(
        `Restore ${list.length} entries?\n\n` +
        `OK  → MERGE mode (safe): only inserts entries whose ID does not already exist.\n` +
        `Cancel → will ask you next to choose REPLACE (overwrite existing).`
      ) ? "merge" : (window.confirm("Use REPLACE mode?\n\nThis OVERWRITES existing entries with same IDs. Continue?") ? "replace" : null);
      if (!mode) { toast.info("Restore cancelled"); return; }
      const r = await api.post(`/admin/quick-entries/restore`, { entries: list, mode });
      toast.success(`Restored: ${r.data.inserted} inserted · ${r.data.updated} updated · ${r.data.skipped} skipped (${mode})`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed — is this a valid backup JSON?");
    } finally {
      if (restoreInputRef.current) restoreInputRef.current.value = "";
    }
  };
  const act = async (id, action) => {
    try { await api.post(`/admin/quick-entries/${id}/${action}`); toast.success(action === "approve" ? "Approved" : "Rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const convertOne = async (id) => {
    if (!window.confirm("Convert this quick entry into real tournament entries?\n\nThis will:\n • Create/link a player record + JPA ID\n • Insert entries into the Organizer Hub pipeline\n • Cannot be undone")) return;
    try {
      const r = await api.post(`/admin/quick-entries/${id}/convert`, { delete_aadhaar: deleteAadhaar });
      toast.success(`Converted → ${r.data.entries_created} real entries · Player ${r.data.jpa_id}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const convertAll = async () => {
    const approved = entries.filter((e) => e.status === "approved");
    if (!approved.length) { toast.info("No APPROVED entries to convert."); return; }
    // Works even on "All tournaments" — groups approved entries per tournament
    const byT = {};
    approved.forEach((e) => { (byT[e.tournament_id] = byT[e.tournament_id] || []).push(e); });
    const tids = Object.keys(byT);
    if (!window.confirm(`Convert all ${approved.length} APPROVED quick entries (${tids.length} tournament${tids.length > 1 ? "s" : ""})?`)) return;
    try {
      let totalConv = 0, totalRows = 0;
      for (const t of tids) {
        const r = await api.post(`/admin/tournaments/${t}/quick-entries/convert-approved`, { delete_aadhaar: deleteAadhaar });
        totalConv += r.data.converted; totalRows += r.data.entries_created;
      }
      toast.success(`Converted ${totalConv} entries → ${totalRows} real rows`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const registerPlayer = async (id, name) => {
    if (!window.confirm(`Register ${name} as a JPA player?\n\nThis will:\n • Issue a JPA ID + auto-generate the player card\n • Add them to the Players tab (editable there)\n • Mark this quick-entry as "registered"\n • NOT create tournament entries (use Convert for that)`)) return;
    try {
      const r = await api.post(`/admin/quick-entries/${id}/register-player`, { delete_aadhaar: deleteAadhaar });
      setRegisteredModal({ name, jpa_id: r.data.jpa_id, card_url: r.data.card_url });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this quick entry?")) return;
    try { await api.delete(`/admin/quick-entries/${id}`); load(); } catch { /* noop */ }
  };
  const openMedia = async (q, field, label) => {
    try {
      const r = await api.get(`/admin/quick-entries/${q.id}/media/${field}`);
      if (r.data?.data_url) setReceiptModal({ url: r.data.data_url, label });
      else toast.info("No image stored");
    } catch { toast.error("Failed to load image"); }
  };
  const mediaBtn = (q, flag, field, label) => q[flag] ? (
    <button key={field} data-testid={`qe-media-${field}-${q.id}`} onClick={() => openMedia(q, field, label)} className="px-3 h-9 border border-jpa-border rounded-sm text-xs font-bold uppercase tracking-widest hover:border-jpa-primary hover:text-jpa-primary transition-colors">
      {label}
    </button>
  ) : null;
  const saveEdit = async () => {
    if (!editingEntry) return;
    const patch = {
      name: editingEntry.name,
      whatsapp: (editingEntry.whatsapp || "").replace(/\D/g, ""),
      email: editingEntry.email,
      gender: editingEntry.gender,
      dob: editingEntry.dob,
      tshirt_size: editingEntry.tshirt_size || null,
      aadhaar_number: (editingEntry.aadhaar_number || "").replace(/\D/g, "") || null,
      notes: editingEntry.notes || null,
      total_fee: Number(editingEntry.total_fee) || 0,
      jpa_id: editingEntry.jpa_id || null,
      pgid: editingEntry.pgid || null,
      dupr_id: editingEntry.dupr_id || null,
      status: editingEntry.status || undefined,
      selections: (editingEntry.selections || []).map((s) => ({
        event_category: s.event_category,
        age_category: s.age_category,
        partner_name: s.partner_name || null,
        partner_whatsapp: s.partner_whatsapp || null,
        partner_aadhaar: s.partner_aadhaar || null,
        needs_partner: !!s.needs_partner,
      })),
    };
    if (patch.aadhaar_number && !/^\d{12}$/.test(patch.aadhaar_number)) {
      toast.error("Aadhaar must be exactly 12 digits"); return;
    }
    if (!patch.selections.length) { toast.error("At least one event is required"); return; }
    const seen = new Set();
    for (const s of patch.selections) {
      if (!s.event_category || !s.age_category) { toast.error("Every event needs an event type and age category"); return; }
      const k = `${s.event_category}__${s.age_category}`;
      if (seen.has(k)) { toast.error(`Duplicate event: ${s.event_category} · ${s.age_category}`); return; }
      seen.add(k);
    }
    try {
      await api.put(`/admin/quick-entries/${editingEntry.id}`, patch);
      toast.success("Entry updated");
      setEditingEntry(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Suggested fee based on tournament fee tiers (or add-on flat rate)
  const suggestedFee = (() => {
    if (!editingEntry) return null;
    const t = tournaments.find((x) => x.id === editingEntry.tournament_id);
    if (!t) return null;
    const n = (editingEntry.selections || []).length;
    if (!n) return 0;
    if (editingEntry.add_on) {
      return n * (Number(t.qr_addon_fee) || 500);
    }
    const tiers = t.qr_fee_tiers || {};
    const key = String(n);
    if (tiers[key] != null) return Number(tiers[key]);
    const keys = Object.keys(tiers).map(Number).filter((k) => !isNaN(k)).sort((a, b) => a - b);
    if (!keys.length) return null;
    const highest = keys[keys.length - 1];
    if (n <= highest) return Number(tiers[String(highest)]);
    return Number(tiers[String(highest)]) + (n - highest) * (Number(t.qr_addon_fee) || 500);
  })();
  const linkPartner = async (selection_index, partner_aadhaar) => {
    const num = (partner_aadhaar || "").replace(/\D/g, "");
    if (!/^\d{12}$/.test(num)) { toast.error("Partner Aadhaar must be 12 digits"); return; }
    try {
      const r = await api.post(`/admin/quick-entries/${editingEntry.id}/link-partner`, {
        selection_index, partner_aadhaar: num,
      });
      if (r.data.linked) toast.success(r.data.message);
      else toast.info(r.data.message);
      const fresh = await api.get(`/admin/quick-entries?tournament_id=${editingEntry.tournament_id}`);
      const updated = fresh.data.find((e) => e.id === editingEntry.id);
      if (updated) setEditingEntry({ ...updated });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const evtLabels = { mens_singles: "MS", mens_doubles: "MD", womens_singles: "WS", womens_doubles: "WD", mixed_doubles: "MxD" };
  const ageLbl = (a) => (a === "19+" ? "Open (19+)" : a);
  const evtLabelsFull = { mens_singles: "Men's Singles", mens_doubles: "Men's Doubles", womens_singles: "Women's Singles", womens_doubles: "Women's Doubles", mixed_doubles: "Mixed Doubles" };
  const evtLabelsMessage = { mens_singles: "Mens Singles", mens_doubles: "Mens Doubles", womens_singles: "Womens Singles", womens_doubles: "Womens Doubles", mixed_doubles: "Mixed Doubles" };
  const buildWhatsAppLink = (q) => {
    const t = tournaments.find((x) => x.id === q.tournament_id);
    const tname = (t && t.name) || "the 5th Jharkhand State Pickleball Tournament";
    const events = (q.selections || []).map((s, i) => `${i + 1}. ${ageLbl(s.age_category)} ${evtLabelsMessage[s.event_category] || s.event_category}`).join("\n");
    const msg =
`Hi ${q.name}, your Registration for the ${tname} is confirmed. 
You Are Participating In Following Events
${events}

Notes
1. Please Ask All Your Partners to Register Separately before end Date.
2. All Your Partners Needs to Pay Separate Fees to register.
3. Please Add Yourself To Players group Using This Link 
https://chat.whatsapp.com/BG913Y5ynOTFZ424vTroWU

Thank you!`;
    return `https://wa.me/${String(q.whatsapp || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`;
  };
  const availableEventOptions = (() => {
    if (!editingEntry) return [];
    const t = tournaments.find((x) => x.id === editingEntry.tournament_id);
    return (t && t.qr_event_categories) || ["mens_singles", "womens_singles", "mens_doubles", "womens_doubles", "mixed_doubles"];
  })();
  const availableAgeOptions = (() => {
    if (!editingEntry) return [];
    const t = tournaments.find((x) => x.id === editingEntry.tournament_id);
    return (t && t.qr_age_categories) || ["U-12", "U-14", "U-16", "U-19", "Open", "35+", "45+", "50+", "55+", "60+", "70+"];
  })();
  const addSelection = () => {
    if (!editingEntry) return;
    const ev = availableEventOptions[0] || "mens_singles";
    const ag = availableAgeOptions[0] || "Open";
    setEditingEntry({ ...editingEntry, selections: [...(editingEntry.selections || []), { event_category: ev, age_category: ag, needs_partner: false }] });
  };
  const removeSelection = (idx) => {
    if (!editingEntry) return;
    const sels = [...(editingEntry.selections || [])];
    sels.splice(idx, 1);
    setEditingEntry({ ...editingEntry, selections: sels });
  };
  const updateSelection = (idx, patch) => {
    if (!editingEntry) return;
    const sels = [...(editingEntry.selections || [])];
    sels[idx] = { ...sels[idx], ...patch };
    setEditingEntry({ ...editingEntry, selections: sels });
  };
  return (
    <div data-testid="admin-quick-entries" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display text-2xl tracking-wider">
          Quick Entries {visibleEntries.length ? `· ${visibleEntries.length} registrations` : ""}
          {visibleEntries.length ? <span className="ml-2 text-sm text-jpa-muted font-body">({eventEntryCount} event entries · {pairAdjustedCount} pair-adjusted)</span> : null}
        </h3>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={deleteAadhaar} onChange={(e) => setDeleteAadhaar(e.target.checked)} data-testid="qe-delete-aadhaar-toggle" className="w-4 h-4 accent-jpa-primary" />
            Auto-delete Aadhaar on convert
          </label>
          <Button data-testid="qe-report-pdf" onClick={() => exportReport("pdf")} size="sm" className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs font-bold">Report (PDF)</Button>
          <Button data-testid="qe-report-csv" onClick={() => exportReport("csv")} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">Report (CSV)</Button>
          <Button data-testid="qe-export-csv" onClick={exportCsv} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">Raw CSV</Button>
          <Button data-testid="qe-export-json" onClick={exportJson} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold">Backup (JSON)</Button>
          <Button data-testid="qe-restore" onClick={() => restoreInputRef.current?.click()} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold border-amber-500 text-amber-700 hover:bg-amber-50">Restore…</Button>
          <input ref={restoreInputRef} type="file" accept="application/json,.json" className="hidden" onChange={(e) => onRestoreFile(e.target.files?.[0])} data-testid="qe-restore-file" />
          <Button data-testid="qe-bulk-template" onClick={downloadBulkTemplate} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold border-sky-500 text-sky-700 hover:bg-sky-50">Sample CSV</Button>
          <Button data-testid="qe-bulk-upload" onClick={() => bulkInputRef.current?.click()} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold border-emerald-500 text-emerald-700 hover:bg-emerald-50">Bulk Upload CSV</Button>
          <input ref={bulkInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { onBulkUpload(e.target.files?.[0]); e.target.value = ""; }} data-testid="qe-bulk-file" />
          <Button data-testid="qe-find-duplicates" onClick={findDuplicates} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold border-red-400 text-red-600 hover:bg-red-50">Find Duplicates</Button>
          <Button data-testid="qe-convert-all" onClick={convertAll} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-widest text-xs font-bold">Convert all approved</Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="qe-status-filter" className="rounded-sm w-[150px]"><SelectValue placeholder="Any status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="registered">Registered</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ageFilter} onValueChange={setAgeFilter}>
            <SelectTrigger data-testid="qe-age-filter" className="rounded-sm w-[140px]"><SelectValue placeholder="All ages" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ages</SelectItem>
              {availableAges.map((a) => <SelectItem key={a} value={a}>{a === "19+" ? "Open (19+)" : a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger data-testid="qe-event-filter" className="rounded-sm w-[170px]"><SelectValue placeholder="All events" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {availableEvents.map((e) => <SelectItem key={e} value={e}>{evtLabelsFull[e] || e}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="qe-tournament-filter" className="rounded-sm max-w-xs"><SelectValue placeholder="All tournaments" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tournaments</SelectItem>
              {tournaments.filter((t) => t.quick_registration).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {visibleEntries.length === 0 ? (
        <div className="border border-dashed border-jpa-border rounded-sm p-10 text-center text-jpa-muted text-sm">No quick entries yet.</div>
      ) : visibleEntries.map((q) => (
        <div key={q.id} data-testid={`qe-row-${q.id}`} className="border border-jpa-border rounded-sm p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-lg">
                {q.name}
                {q.add_on && <span className="ml-2 text-[10px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm">ADD-ON</span>}
                <span className={`ml-2 text-xs font-normal px-2 py-0.5 rounded-sm ${
                  q.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                  q.status === "converted" ? "bg-blue-100 text-blue-700" :
                  q.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
                }`}>{q.status.toUpperCase()}</span>
                {q.converted_jpa_id && <span className="ml-2 text-xs font-mono text-jpa-primary">→ {q.converted_jpa_id}</span>}
              </div>
              <div className="text-xs text-jpa-muted mt-1">
                {q.email} · {q.whatsapp} · {q.gender === "M" ? "Male" : "Female"} · DOB {q.dob}
                {q.tshirt_size ? ` · Size ${q.tshirt_size}` : ""}
                {q.aadhaar_number ? ` · Aadhaar ${q.aadhaar_number}` : ""}
                {q.jpa_id ? ` · JPA ${q.jpa_id}` : ""}{q.pgid ? ` · PG ${q.pgid}` : ""}{q.dupr_id ? ` · DUPR ${q.dupr_id}` : ""}
              </div>
              <div className="text-xs mt-2 flex flex-wrap gap-1">
                {q.selections?.map((s, i) => {
                  const isDoubles = (s.event_category || "").includes("doubles");
                  const partnerTxt = s.partner_name ? `${q.name} / ${s.partner_name}` : (s.needs_partner ? `${q.name} / NEEDS PARTNER` : q.name);
                  return (
                    <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm ${s.needs_partner ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-jpa-primary/10 text-jpa-primary"}`}>
                      {evtLabels[s.event_category]} · {ageLbl(s.age_category)}
                      {isDoubles ? <span className="text-jpa-ink/70 font-normal">· {partnerTxt}</span> : null}
                    </span>
                  );
                })}
              </div>
              <div className="text-sm mt-2">Fee: <b className="text-jpa-primary">₹{q.total_fee}</b>{q.notes ? ` · Note: ${q.notes}` : ""}</div>
            </div>
            <div className="flex gap-2 items-center flex-shrink-0">
              {mediaBtn(q, "has_aadhaar_front", "aadhaar_front", "Aadhaar front")}
              {mediaBtn(q, "has_aadhaar_back", "aadhaar_back", "Aadhaar back")}
              {mediaBtn(q, "has_receipt", "receipt", "Receipt")}
              {mediaBtn(q, "has_photo", "photo", "Photo")}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 justify-end">
            <a data-testid={`qe-whatsapp-${q.id}`} href={buildWhatsAppLink(q)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#25D366] text-white text-xs font-bold uppercase tracking-widest">WhatsApp</a>
            <Button data-testid={`qe-edit-${q.id}`} onClick={() => setEditingEntry({ ...q })} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs font-bold"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
            {q.status === "pending" && <Button data-testid={`qe-approve-${q.id}`} onClick={() => act(q.id, "approve")} size="sm" className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs">Approve</Button>}
            {q.status !== "rejected" && q.status !== "converted" && q.status !== "registered" && <Button data-testid={`qe-reject-${q.id}`} onClick={() => act(q.id, "reject")} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs">Reject</Button>}
            {q.status !== "converted" && q.status !== "registered" && <Button data-testid={`qe-register-${q.id}`} onClick={() => registerPlayer(q.id, q.name)} size="sm" className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-white uppercase tracking-widest text-xs">Register (Player + Card)</Button>}
            {q.status === "approved" && <Button data-testid={`qe-convert-${q.id}`} onClick={() => convertOne(q.id)} size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-widest text-xs">Convert to Organizer Hub</Button>}
            {q.converted_player_id && q.jpa_id_generated_url && <a href={q.jpa_id_generated_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-jpa-border text-xs uppercase tracking-widest font-bold hover:border-jpa-primary">Download card ↗</a>}
            <Button data-testid={`qe-delete-${q.id}`} onClick={() => del(q.id)} size="sm" variant="outline" className="rounded-sm uppercase tracking-widest text-xs text-jpa-live"><Trash2 className="w-3 h-3" /></Button>
          </div>
        </div>
      ))}

      {bulkResult && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setBulkResult(null)}>
          <div data-testid="qe-bulk-result-modal" className="bg-white rounded-sm max-w-lg w-full p-5 border border-jpa-border max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-xl tracking-wider">Bulk upload result</h4>
              <button data-testid="qe-bulk-result-close" onClick={() => setBulkResult(null)} className="p-1 text-jpa-muted hover:text-jpa-ink text-lg leading-none">✕</button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4 text-center">
              <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-2"><div className="font-display text-2xl text-emerald-700">{bulkResult.created}</div><div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Created</div></div>
              <div className="border border-amber-300 bg-amber-50 rounded-sm p-2"><div className="font-display text-2xl text-amber-700">{bulkResult.skipped}</div><div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Duplicates skipped</div></div>
              <div className="border border-red-300 bg-red-50 rounded-sm p-2"><div className="font-display text-2xl text-red-700">{bulkResult.errors?.length || 0}</div><div className="text-[10px] font-bold uppercase tracking-widest text-red-700">Row errors</div></div>
            </div>
            {(bulkResult.errors || []).length > 0 && (
              <div className="border border-red-200 rounded-sm divide-y divide-red-100 max-h-64 overflow-y-auto">
                {bulkResult.errors.map((e, i) => (
                  <div key={i} data-testid={`qe-bulk-error-${i}`} className="px-3 py-2 text-xs"><span className="font-mono font-bold text-red-700">Row {e.row}:</span> <span className="text-jpa-ink">{e.reason}</span></div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-jpa-muted mt-3">Created entries are APPROVED and already visible in Organizer Hub (run Auto-sync / Import entries to use them). Fix error rows in your CSV and upload again — duplicates are auto-skipped.</p>
            <div className="flex justify-end mt-4">
              <Button data-testid="qe-bulk-result-ok" onClick={() => setBulkResult(null)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Done</Button>
            </div>
          </div>
        </div>
      )}
      {dupGroups && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setDupGroups(null)}>
          <div data-testid="qe-duplicates-modal" className="bg-white rounded-sm max-w-2xl w-full p-5 border border-jpa-border max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-display text-xl tracking-wider">Duplicate scan</h4>
              <button data-testid="qe-duplicates-close" onClick={() => setDupGroups(null)} className="p-1 text-jpa-muted hover:text-jpa-ink text-lg leading-none">✕</button>
            </div>
            {dupGroups.total_groups === 0 ? (
              <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-4 text-sm text-emerald-800 font-bold text-center">No duplicates found — all entries are unique by Aadhaar, WhatsApp and name.</div>
            ) : (
              <div className="space-y-4">
                <p className="text-xs text-jpa-muted">{dupGroups.total_groups} duplicate group(s) found. "Keep oldest, delete rest" keeps the FIRST registration and removes the others (their tournament entries are removed too).</p>
                {dupGroups.groups.map((g, gi) => (
                  <div key={g.key} data-testid={`qe-dup-group-${gi}`} className="border border-red-200 rounded-sm overflow-hidden">
                    <div className="bg-red-50 px-3 py-2 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-widest text-red-700">{g.kind} · {g.entries.length} rows</span>
                      <Button data-testid={`qe-dup-keep-${gi}`} size="sm" onClick={() => dedupeGroup(g)} className="rounded-sm bg-red-600 hover:bg-red-700 text-white text-[10px] uppercase tracking-widest font-bold h-7">Keep oldest, delete rest</Button>
                    </div>
                    <div className="divide-y divide-jpa-border/50">
                      {g.entries.map((e, ei) => (
                        <div key={e.id} className="px-3 py-2 text-xs flex items-start justify-between gap-2">
                          <div>
                            <div className="font-bold text-sm">{e.name} {ei === 0 && <span className="text-[9px] font-bold uppercase tracking-widest bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm ml-1">oldest</span>}</div>
                            <div className="text-jpa-muted font-mono">{e.aadhaar_number || "no aadhaar"} · {e.whatsapp || "no phone"} · ₹{e.total_fee}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {e.events.map((ev, i2) => <span key={i2} className="text-[10px] bg-jpa-primary/10 text-jpa-primary px-1.5 py-0.5 rounded-sm">{ev}</span>)}
                            </div>
                          </div>
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${e.status === "approved" ? "bg-emerald-100 text-emerald-700" : e.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-jpa-surface text-jpa-muted"}`}>{e.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button data-testid="qe-dup-done" onClick={() => setDupGroups(null)} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">Done</Button>
            </div>
          </div>
        </div>
      )}
      {receiptModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setReceiptModal(null)}>
          <div className="bg-white rounded-sm p-4 max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold mb-2">{receiptModal.label}</div>
            <img src={receiptModal.url} alt="" className="max-w-full max-h-[75vh] object-contain" />
          </div>
        </div>
      )}

      {registeredModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setRegisteredModal(null)}>
          <div data-testid="qe-registered-modal" className="bg-white rounded-sm p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl tracking-wider text-jpa-primary mb-2">Player registered ✓</h3>
            <p className="text-sm text-jpa-muted mb-4">
              <b>{registeredModal.name}</b> is now a JPA player with ID <b className="font-mono">{registeredModal.jpa_id}</b>. Their card is auto-generated — copy the link below and share on WhatsApp.
            </p>
            <div className="border border-jpa-border rounded-sm p-3 bg-jpa-surface break-all text-xs font-mono">{registeredModal.card_url}</div>
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold" onClick={() => { navigator.clipboard.writeText(registeredModal.card_url); toast.success("Card link copied"); }}>
                Copy link
              </Button>
              <a href={registeredModal.card_url} target="_blank" rel="noreferrer" className="inline-flex items-center px-4 py-2 rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white text-xs uppercase tracking-widest font-bold">
                Open card ↗
              </a>
              <Button className="rounded-sm text-xs uppercase tracking-widest font-bold bg-jpa-ink text-white" onClick={() => setRegisteredModal(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}

      {editingEntry && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditingEntry(null)}>
          <div data-testid="qe-edit-modal" className="bg-white rounded-sm p-6 max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-2xl tracking-wider text-jpa-primary">Edit quick entry</h3>
                <p className="text-xs text-jpa-muted mt-1">Update player details — useful to add Aadhaar number to old records.</p>
              </div>
              <button onClick={() => setEditingEntry(null)} className="w-8 h-8 grid place-items-center rounded-sm hover:bg-jpa-surface" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="qe-edit-name" value={editingEntry.name || ""} onChange={(e) => setEditingEntry({ ...editingEntry, name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>WhatsApp (10 digits)</Label><Input data-testid="qe-edit-whatsapp" inputMode="numeric" maxLength={10} value={editingEntry.whatsapp || ""} onChange={(e) => setEditingEntry({ ...editingEntry, whatsapp: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="mt-1 rounded-sm" /></div>
              <div><Label>Email</Label><Input data-testid="qe-edit-email" type="email" value={editingEntry.email || ""} onChange={(e) => setEditingEntry({ ...editingEntry, email: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div>
                <Label>Gender</Label>
                <Select value={editingEntry.gender || "M"} onValueChange={(v) => setEditingEntry({ ...editingEntry, gender: v })}>
                  <SelectTrigger data-testid="qe-edit-gender" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="M">Male</SelectItem><SelectItem value="F">Female</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>DOB</Label><Input data-testid="qe-edit-dob" type="date" value={editingEntry.dob || ""} onChange={(e) => setEditingEntry({ ...editingEntry, dob: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div>
                <Label>T-shirt size</Label>
                <Select value={editingEntry.tshirt_size || ""} onValueChange={(v) => setEditingEntry({ ...editingEntry, tshirt_size: v })}>
                  <SelectTrigger data-testid="qe-edit-tshirt" className="mt-1 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Small">Small</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Large">Large</SelectItem>
                    <SelectItem value="XL">XL</SelectItem>
                    <SelectItem value="XXL">XXL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2"><Label>Aadhaar number (12 digits)</Label><Input data-testid="qe-edit-aadhaar" inputMode="numeric" maxLength={12} value={editingEntry.aadhaar_number || ""} onChange={(e) => setEditingEntry({ ...editingEntry, aadhaar_number: e.target.value.replace(/\D/g, "").slice(0, 12) })} placeholder="XXXX XXXX XXXX" className="mt-1 rounded-sm font-mono tracking-widest" /></div>
              <div><Label>Status</Label>
                <Select value={editingEntry.status || "pending"} onValueChange={(v) => setEditingEntry({ ...editingEntry, status: v })}>
                  <SelectTrigger data-testid="qe-edit-status" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="registered">Registered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>JPA ID (opt)</Label><Input data-testid="qe-edit-jpa" value={editingEntry.jpa_id || ""} onChange={(e) => setEditingEntry({ ...editingEntry, jpa_id: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div>
                <Label>
                  Total fee (₹)
                  {suggestedFee != null ? <span className="ml-2 text-[10px] font-normal text-jpa-muted">suggested ₹{suggestedFee}</span> : null}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input data-testid="qe-edit-fee" type="number" min={0} value={editingEntry.total_fee ?? 0} onChange={(e) => setEditingEntry({ ...editingEntry, total_fee: e.target.value })} className="rounded-sm" />
                  {suggestedFee != null && Number(editingEntry.total_fee) !== suggestedFee ? (
                    <Button type="button" data-testid="qe-edit-use-suggested" size="sm" variant="outline" className="rounded-sm text-xs uppercase tracking-widest font-bold" onClick={() => setEditingEntry({ ...editingEntry, total_fee: suggestedFee })}>
                      Use ₹{suggestedFee}
                    </Button>
                  ) : null}
                </div>
              </div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={2} data-testid="qe-edit-notes" value={editingEntry.notes || ""} onChange={(e) => setEditingEntry({ ...editingEntry, notes: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>

            <div className="mt-6 border-t border-jpa-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-display text-lg tracking-wider">Event categories</h4>
                <Button type="button" size="sm" data-testid="qe-edit-add-selection" onClick={addSelection} className="rounded-sm text-xs uppercase tracking-widest font-bold bg-jpa-primary hover:bg-jpa-primary-hover text-white">
                  <Plus className="w-3 h-3 mr-1" /> Add event
                </Button>
              </div>
              <p className="text-xs text-jpa-muted mb-3">Change, add or remove any event this player is registered for. Fee is not auto-updated — use the suggested value above if you want it recalculated.</p>
              {(!editingEntry.selections || editingEntry.selections.length === 0) ? (
                <div className="text-xs text-red-600 border border-dashed border-red-300 rounded-sm p-3 bg-red-50">At least one event is required.</div>
              ) : (
                <div className="space-y-2">
                  {(editingEntry.selections || []).map((s, i) => (
                    <div key={i} data-testid={`qe-edit-selection-${i}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end border border-jpa-border rounded-sm p-2 bg-white">
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-jpa-muted">Event type</Label>
                        <Select value={s.event_category} onValueChange={(v) => updateSelection(i, { event_category: v })}>
                          <SelectTrigger data-testid={`qe-edit-selection-event-${i}`} className="mt-1 rounded-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {availableEventOptions.map((ev) => <SelectItem key={ev} value={ev}>{evtLabelsFull[ev] || ev}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-widest text-jpa-muted">Age category</Label>
                        <Select value={s.age_category} onValueChange={(v) => updateSelection(i, { age_category: v })}>
                          <SelectTrigger data-testid={`qe-edit-selection-age-${i}`} className="mt-1 rounded-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {availableAgeOptions.map((a) => <SelectItem key={a} value={a}>{a === "19+" ? "Open (19+)" : a}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="button" size="sm" variant="outline" data-testid={`qe-edit-remove-selection-${i}`} onClick={() => removeSelection(i)} className="rounded-sm text-xs uppercase tracking-widest font-bold text-jpa-live h-9">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(editingEntry.selections || []).some((s) => (s.event_category || "").includes("doubles")) && (
              <div className="mt-6 border-t border-jpa-border pt-4">
                <h4 className="font-display text-lg tracking-wider mb-2">Doubles partners</h4>
                <p className="text-xs text-jpa-muted mb-3">Enter partner&apos;s 12-digit Aadhaar & click Link — if that player also has a quick entry, both records are paired automatically.</p>
                <div className="space-y-3">
                  {(editingEntry.selections || []).map((s, i) => {
                    if (!(s.event_category || "").includes("doubles")) return null;
                    const label = evtLabels[s.event_category] || s.event_category;
                    const partnerAad = s.partner_aadhaar || "";
                    return (
                      <div key={i} data-testid={`qe-partner-row-${i}`} className="border border-jpa-border rounded-sm p-3 bg-jpa-surface">
                        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                          <div className="text-sm font-bold">{label} · {ageLbl(s.age_category)}</div>
                          <div className="text-[11px] text-jpa-muted">
                            {s.partner_name ? <>Paired with <b>{s.partner_name}</b> ({s.partner_whatsapp || "no wa"})</> : s.needs_partner ? <span className="text-amber-700 font-bold">NEEDS PARTNER</span> : "No partner yet"}
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-2 mb-2">
                          <Input
                            data-testid={`qe-partner-name-${i}`}
                            placeholder="Partner name (exact registered spelling)"
                            value={s.partner_name || ""}
                            onChange={(e) => updateSelection(i, { partner_name: e.target.value })}
                            className="rounded-sm h-10"
                          />
                          <Input
                            data-testid={`qe-partner-phone-${i}`}
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="Partner WhatsApp (10 digits)"
                            value={s.partner_whatsapp || ""}
                            onChange={(e) => updateSelection(i, { partner_whatsapp: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                            className="rounded-sm h-10 font-mono"
                          />
                        </div>
                        <div className="flex flex-col md:flex-row gap-2">
                          <Input
                            data-testid={`qe-partner-aadhaar-${i}`}
                            inputMode="numeric"
                            maxLength={12}
                            placeholder="Partner Aadhaar (12 digits)"
                            defaultValue={partnerAad}
                            id={`partner-aad-${i}`}
                            className="rounded-sm h-10 font-mono flex-1"
                          />
                          <Button
                            type="button"
                            size="sm"
                            data-testid={`qe-link-partner-${i}`}
                            onClick={() => {
                              const el = document.getElementById(`partner-aad-${i}`);
                              linkPartner(i, el?.value || "");
                            }}
                            className="rounded-sm text-xs uppercase tracking-widest font-bold bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-4"
                          >
                            Link partner
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" data-testid="qe-edit-cancel" onClick={() => setEditingEntry(null)} className="rounded-sm text-xs uppercase tracking-widest font-bold">Cancel</Button>
              <Button data-testid="qe-edit-save" onClick={saveEdit} className="rounded-sm text-xs uppercase tracking-widest font-bold bg-jpa-primary hover:bg-jpa-primary-hover text-white">Save changes</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Player Manager (admin editable) ----------
const DISTRICTS_ALL = ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Hazaribagh", "Deoghar", "Giridih", "Palamu", "Chaibasa", "Dumka"];

function PhotoField({ label, value, onChange }) {
  const uploadRef = useRef();
  const cameraRef = useRef();
  const onFile = (f) => {
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Max 3 MB"); return; }
    const r = new FileReader();
    r.onload = () => onChange(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        {value ? <img src={value} alt="" className="w-20 h-20 object-cover border border-jpa-border rounded-sm" /> : <div className="w-20 h-20 border border-dashed border-jpa-border rounded-sm" />}
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => cameraRef.current?.click()} className="text-xs px-2 py-1 border border-jpa-border rounded-sm hover:border-jpa-primary">📷 Camera</button>
          <button type="button" onClick={() => uploadRef.current?.click()} className="text-xs px-2 py-1 border border-jpa-border rounded-sm hover:border-jpa-primary">📤 Upload</button>
          {value && <button type="button" onClick={() => onChange(null)} className="text-xs px-2 py-1 text-jpa-live">Remove</button>}
        </div>
      </div>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      <input ref={uploadRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
    </div>
  );
}

function PlayerManagerPanel({ players, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const filtered = players.filter((p) => !search || `${p.name} ${p.email} ${p.jpa_id} ${p.phone}`.toLowerCase().includes(search.toLowerCase()));
  const open = (p) => { setEditing(p); setCreating(false); setForm({
    name: p.name || "", email: p.email || "", phone: p.phone || "", gender: p.gender || "M",
    dob: (p.dob || "").slice(0, 10), district: p.district || "Ranchi",
    dupr_id: p.dupr_id || "", pgid: p.pgid || "", status: p.status || "approved",
    photo_data_url: p.photo_data_url || null, aadhaar_front_data_url: p.aadhaar_front_data_url || null, aadhaar_back_data_url: p.aadhaar_back_data_url || null,
  }); };
  const openCreate = () => {
    setCreating(true); setEditing({ id: null, jpa_id: null, created_at: null });
    setForm({ name: "", email: "", phone: "", gender: "M", dob: "", district: "Ranchi", dupr_id: "", pgid: "", status: "approved", photo_data_url: null });
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.phone) payload.phone = payload.phone.replace(/\D/g, "").slice(0, 10);
      if (creating) {
        const r = await api.post("/admin/players", payload);
        toast.success(`Player created — ${r.data.jpa_id}`);
      } else {
        await api.put(`/admin/players/${editing.id}`, payload);
        toast.success("Player updated");
      }
      setEditing(null);
      setCreating(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this player?")) return;
    try { await api.delete(`/players/${id}`); onChanged?.(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="admin-player-manager" className="border border-jpa-border rounded-sm p-6">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="font-display text-2xl tracking-wider">Manage players ({players.length})</h3>
        <div className="flex items-center gap-2">
          <Input placeholder="Search name / JPA ID / email / phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pm-search" className="max-w-sm rounded-sm" />
          <Button data-testid="pm-add-player" onClick={openCreate} className="rounded-sm bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest text-xs font-bold whitespace-nowrap"><Plus className="w-4 h-4 mr-1" /> Add player</Button>
        </div>
      </div>
      <div className="border border-jpa-border">
        <div className="grid grid-cols-12 gap-2 py-2 px-3 bg-jpa-surface text-[10px] font-bold uppercase tracking-widest">
          <div className="col-span-1">Photo</div>
          <div className="col-span-3">Name / JPA ID</div>
          <div className="col-span-3">Contact</div>
          <div className="col-span-2">District</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="max-h-[500px] overflow-auto">
          {filtered.map((p) => (
            <div key={p.id} data-testid={`pm-row-${p.id}`} className="grid grid-cols-12 gap-2 py-2 px-3 border-t border-jpa-border text-sm items-center">
              <div className="col-span-1">{p.photo_data_url ? <img src={p.photo_data_url} alt="" className="w-10 h-10 object-cover rounded-sm border border-jpa-border" /> : <div className="w-10 h-10 bg-jpa-surface rounded-sm" />}</div>
              <div className="col-span-3 truncate"><div className="font-bold truncate">{p.name}</div><div className="text-xs text-jpa-muted">{p.jpa_id || "—"}</div></div>
              <div className="col-span-3 text-xs truncate">{p.email || "—"}<br />{p.phone || "—"}</div>
              <div className="col-span-2 truncate">{p.district}</div>
              <div className="col-span-1"><span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${p.status === "approved" ? "bg-emerald-100 text-emerald-700" : p.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{(p.status || "pending").toUpperCase()}</span></div>
              <div className="col-span-2 flex gap-1 justify-end">
                <button data-testid={`pm-edit-${p.id}`} onClick={() => open(p)} className="text-jpa-primary hover:text-jpa-primary-hover p-1" title="Edit"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`pm-delete-${p.id}`} onClick={() => del(p.id)} className="text-jpa-live hover:text-red-700 p-1" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div data-testid="pm-edit-modal" className="bg-white rounded-sm p-6 max-w-3xl w-full max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4 gap-3">
              <div>
                <h3 className="font-display text-2xl tracking-wider">{creating ? "Add player" : "Edit player"}</h3>
                <div className="text-xs text-jpa-muted mt-1">{creating ? "JPA ID will be auto-assigned on save" : <>JPA ID <span className="font-mono">{editing.jpa_id || "—"}</span> · Created {String(editing.created_at || "").slice(0, 10)}</>}</div>
              </div>
              <button onClick={() => setEditing(null)} className="w-8 h-8 grid place-items-center rounded-sm hover:bg-jpa-surface"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Phone (10 digits)</Label><Input inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="mt-1 rounded-sm" /></div>
              <div><Label>Gender</Label><Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="M">Male</SelectItem><SelectItem value="F">Female</SelectItem></SelectContent></Select></div>
              <div><Label>DOB</Label><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>District</Label><Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v })}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{DISTRICTS_ALL.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>DUPR ID</Label><Input value={form.dupr_id} onChange={(e) => setForm({ ...form, dupr_id: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>PGID</Label><Input value={form.pgid} onChange={(e) => setForm({ ...form, pgid: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div><Label>Approval status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="approved">Approved</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent></Select></div>
            </div>
            <div className="mt-4 grid md:grid-cols-3 gap-3">
              <PhotoField label="Player photo" value={form.photo_data_url} onChange={(v) => setForm({ ...form, photo_data_url: v })} />
              {!creating && (
                <>
                  <PhotoField label="Aadhaar front" value={form.aadhaar_front_data_url} onChange={(v) => setForm({ ...form, aadhaar_front_data_url: v })} />
                  <PhotoField label="Aadhaar back" value={form.aadhaar_back_data_url} onChange={(v) => setForm({ ...form, aadhaar_back_data_url: v })} />
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm uppercase tracking-widest text-xs font-bold">Cancel</Button>
              <Button disabled={saving} onClick={save} data-testid="pm-save" className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm uppercase tracking-widest text-xs font-bold">{saving ? "Saving…" : "Save changes"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

